/**
 * Offline Status Indicator
 *
 * Displays clear offline/online status and read-only mode indicator.
 */

import React from 'react';
import { Chip, Box, Tooltip, Typography } from '@mui/material';
import {
  CloudOff as OfflineIcon,
  CloudDone as OnlineIcon,
  Sync as SyncIcon,
  Warning as ReadOnlyIcon,
  Storage as QueueIcon,
} from '@mui/icons-material';
import { useOfflineStatus } from '../../hooks/useOfflineStatus';
import { offlineQueueService } from '../../services/offline-queue.service';

export const OfflineStatusIndicator: React.FC = () => {
  const status = useOfflineStatus();

  const handleSync = () => {
    offlineQueueService.sync();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {!status.isOnline && (
        <Tooltip title="You are offline. Changes will be queued and synced when back online.">
          <Chip
            icon={<OfflineIcon />}
            label="Offline"
            color="warning"
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}

      {status.isOnline && !status.isReadOnly && (
        <Tooltip title="Connected to server">
          <Chip
            icon={<OnlineIcon />}
            label="Online"
            color="success"
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}

      {status.isReadOnly && (
        <Tooltip title="Viewing cached data. Server unavailable. Some features may be limited.">
          <Chip
            icon={<ReadOnlyIcon />}
            label="Read-only (cached)"
            color="info"
            size="small"
            variant="outlined"
          />
        </Tooltip>
      )}

      {status.pendingQueueCount > 0 && (
        <Tooltip title={`${status.pendingQueueCount} pending changes waiting to sync`}>
          <Chip
            icon={<QueueIcon />}
            label={`${status.pendingQueueCount} pending`}
            size="small"
            variant="outlined"
            onClick={status.isOnline ? handleSync : undefined}
            sx={{ cursor: status.isOnline ? 'pointer' : 'default' }}
          />
        </Tooltip>
      )}

      {status.isSyncing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SyncIcon sx={{ fontSize: 18 }} />
          <Typography variant="caption" color="text.secondary">
            Syncing...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
